import { Document, Font, Image, Page, StyleSheet, Text, View, pdf } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { assetUrl } from '~/constants';
import { formatNumberToFourDp } from '../utils';
import { Invoice, ProductInOrder, UserOrg } from '~/lib/drizzle/type';

type InvoicePdfProps = {
  invoice: Invoice;
  userOrg: UserOrg | null;
  products: ProductInOrder[];
};

Font.register({
  family: 'Noto Serif SC',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/noto-serif-sc@latest/chinese-simplified-400-normal.ttf',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/noto-serif-sc@latest/chinese-simplified-700-normal.ttf',
      fontWeight: 'bold',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/noto-serif-sc@latest/chinese-simplified-900-normal.ttf',
      fontWeight: 'ultrabold',
    },
  ],
});
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/roboto@latest/latin-400-normal.ttf',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/roboto@latest/latin-700-normal.ttf',
      fontWeight: 'bold',
    },
    {
      src: 'https://cdn.jsdelivr.net/fontsource/fonts/roboto@latest/latin-900-normal.ttf',
      fontWeight: 'ultrabold',
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    padding: '24mm 16mm',
    fontFamily: 'Noto Serif SC',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  section: {
    marginTop: '8px',
  },
  table: {
    marginTop: '8px',
    // border: '1px solid #444',
  },
  tableRow: {
    flexDirection: 'row',
  },
  tableCellWithoutBorder: {
    padding: 4,
  },
  tableHeaderLabel: {
    fontSize: 8,
    marginBottom: 2,
  },
  tableHeaderValue: {
    fontSize: 8,
    fontWeight: 'bold',
  },
  tableCell: {
    border: '1px solid #666666',
    padding: '2px',
    fontSize: 8,
  },
  tableHeader: {
    backgroundColor: '#585858',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 8,
  },
  fontFamilyNoto: {
    fontFamily: 'Noto Serif SC',
  },
  fontNormal: {
    fontSize: 8,
  },
  fontWeightBold: {
    fontWeight: 'bold',
  },
  textWhite: {
    color: '#fff',
  },
  box: {
    backgroundColor: '#585858',
    padding: '2px 3px',
    marginBottom: '4px',
    border: '1px solid black',
  },
  signatureSection: {
    borderTop: '2px solid #000000',
    width: '45%',
    textAlign: 'center',
    paddingTop: 12,
  },
});

const InvoicePdf = ({ invoice, userOrg, products }: InvoicePdfProps) => {
  const subtotal = products.reduce(
    (tot: number, curr: ProductInOrder) => tot + (parseFloat(curr?.unit_price?.toFixed(2)) || 0.0),
    0
  );
  const gstPercentage = ((invoice.order?.gst || 0) / subtotal) * 100;
  const FloorGST = gstPercentage ? Math.round(gstPercentage) : 0;

  const checkdata = (data?: string | null) => {
    if (data === 'null' || data === null || data === undefined) {
      return '---';
    }

    return data;
  };

  return (
    <Document>
      <Page size='A4' style={styles.page}>
        <View fixed>
          <View style={styles.header}>
            <View>
              <Text
                style={{
                  fontSize: 20,
                  fontWeight: 'ultrabold',
                  marginBottom: 4,
                }}
              >
                INVOICE
              </Text>
              <Text style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 6 }}>{checkdata(userOrg?.name)}</Text>
              <Text style={{ fontSize: 8, marginBottom: 2 }}>{checkdata(userOrg?.address)}</Text>
              <Text style={{ fontSize: 8, marginBottom: 6 }}>
                TEL: {checkdata(userOrg?.company_phoneNumber)} EMAIL: {checkdata(userOrg?.company_email)}
              </Text>
            </View>
            <View style={[styles.row, { gap: 8 }]}>
              {invoice?.paynow_qr && <Image style={{ width: 100, height: 100 }} src={invoice?.paynow_qr} />}
              {!invoice?.paynow_qr && userOrg?.qrImage && (
                <Image style={{ width: 100, height: 100 }} src={userOrg?.qrImage} />
              )}
              <Image
                style={{ height: 100 }}
                alt='company logo'
                src={userOrg?.logo || `${assetUrl}/rambutan-for-admin.png`}
              />
            </View>
          </View>

          <View style={[styles.section, { padding: '4px 0', backgroundColor: '#f1f1f1' }]}>
            <View style={styles.tableRow}>
              <View style={[styles.tableCellWithoutBorder, { width: '118pt' }]}>
                <Text style={[styles.tableHeaderLabel]}>INVOICE NUMBER</Text>
                <Text style={[styles.tableHeaderValue]}>
                  {invoice.invoiceERP ? checkdata(invoice.invoiceERP) : checkdata(invoice.ref)}
                </Text>
              </View>
              <View style={[styles.tableCellWithoutBorder, { width: '148pt' }]}>
                <Text style={[styles.tableHeaderLabel]}>DELIVERY DATE</Text>
                <Text style={[styles.tableHeaderValue]}>
                  {invoice.order?.delivery_date ? format(new Date(invoice.order?.delivery_date), 'LLL dd, y') : '-'}
                </Text>
              </View>
              <View style={[styles.tableCellWithoutBorder, { width: '146pt' }]}>
                <Text style={[styles.tableHeaderLabel]}>INVOICE DATE</Text>
                <Text style={[styles.tableHeaderValue]}>
                  {invoice.created_at ? format(new Date(invoice.created_at), 'LLL dd, y') : '-'}
                </Text>
              </View>
              <View style={[styles.tableCellWithoutBorder, { width: '107pt' }]}>
                <Text style={[styles.tableHeaderLabel]}>DUE AMOUNT</Text>
                <Text style={[styles.tableHeaderValue]}>$ {checkdata(invoice.due_amount?.toFixed(2))}</Text>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            <View style={[styles.row]}>
              <View style={{ width: '45%' }}>
                <View style={[styles.box]}>
                  <Text style={[styles.fontNormal, styles.textWhite]}>BILL TO:</Text>
                </View>
                <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>
                  {checkdata(invoice.customer?.billing_company_name)}
                </Text>
                <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>
                  {checkdata(invoice.customer?.billing_address)}
                </Text>
              </View>
              <View style={{ width: '45%' }}>
                <View style={[styles.box]}>
                  <Text style={[styles.fontNormal, styles.textWhite]}>DELIVER TO:</Text>
                </View>
                <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>{checkdata(invoice.customer?.name)}</Text>
                <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>{checkdata(invoice.delivery_address)}</Text>
              </View>
            </View>
          </View>

          <View style={styles.section}>
            <View style={[styles.tableRow, { gap: 4 }]}>
              <Text style={[styles.fontNormal, styles.fontWeightBold, styles.fontFamilyNoto]}>Customer ID:</Text>
              <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>{checkdata(invoice.company_id)}</Text>
            </View>
          </View>

          <View style={{ marginTop: 4 }}>
            <View style={[styles.row]}>
              <View style={[styles.tableRow, { width: '45%', gap: 4 }]}>
                <Text style={[styles.fontNormal, styles.fontWeightBold, styles.fontFamilyNoto]}>Payment Terms:</Text>
                <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>{checkdata(invoice.payment_terms)}</Text>
              </View>
              <View style={[styles.tableRow, { width: '45%', gap: 4 }]}>
                <Text style={[styles.fontNormal, styles.fontWeightBold, styles.fontFamilyNoto]}>Truck No:</Text>
                <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>
                  {checkdata(invoice.truck_no || invoice.customer?.truck_no)}
                </Text>
              </View>
            </View>
          </View>

          <View render={({ pageNumber }) => pageNumber > 1 && <View style={{ marginBottom: 16 }}></View>} fixed />
        </View>

        <View style={styles.section}>
          <View style={styles.table}>
            <View style={styles.tableRow}>
              <View style={[styles.tableCell, styles.tableHeader, { width: '5%' }]}>
                <Text>NO.</Text>
              </View>
              <View style={[styles.tableCell, styles.tableHeader, { width: '12.5%' }]}>
                <Text>PRODUCT ID</Text>
              </View>
              <View style={[styles.tableCell, styles.tableHeader, { width: '37.5%' }]}>
                <Text>PRODUCT NAME</Text>
              </View>
              <View style={[styles.tableCell, styles.tableHeader, { width: '10%' }]}>
                <Text style={{ textAlign: 'center' }}>UNIT</Text>
              </View>
              <View style={[styles.tableCell, styles.tableHeader, { width: '10%' }]}>
                <Text style={{ textAlign: 'center' }}>QTY</Text>
              </View>
              <View style={[styles.tableCell, styles.tableHeader, { width: '12%' }]}>
                <Text style={{ textAlign: 'center' }}>PRICE</Text>
              </View>
              <View style={[styles.tableCell, styles.tableHeader, { width: '13%' }]}>
                <Text>AMOUNT (SGD)</Text>
              </View>
            </View>

            {products.map((item: ProductInOrder, index: number) => (
              <View wrap={false} key={index} style={styles.tableRow}>
                <View style={[styles.tableCell, { width: '5%' }]}>
                  <Text>{index + 1}</Text>
                </View>
                <View style={[styles.tableCell, { width: '12.5%' }]}>
                  <Text>{item?.product?.product_ref}</Text>
                </View>
                <View style={[styles.tableCell, { width: '37.5%' }]}>
                  <Text style={[styles.fontFamilyNoto]}>{item?.product_name}</Text>
                  {item?.remarks && (
                    <>
                      <Text style={styles.fontWeightBold}>{`${'\n'}`}Line Remarks: </Text>
                      <Text style={styles.fontFamilyNoto}>{item.remarks}</Text>
                    </>
                  )}
                </View>
                <View style={[styles.tableCell, { width: '10%', textAlign: 'center' }]}>
                  <Text>{item?.unit}</Text>
                </View>
                <View style={[styles.tableCell, { width: '10%', textAlign: 'center' }]}>
                  <Text>{item?.quantity}</Text>
                </View>
                <View style={[styles.tableCell, { width: '12%', textAlign: 'center' }]}>
                  <Text>{formatNumberToFourDp((item?.new_price as number) ?? 0)}</Text>
                </View>
                <View style={[styles.tableCell, { width: '13%', textAlign: 'right' }]}>
                  <Text>{(item?.unit_price as number).toFixed(2)}</Text>
                </View>
              </View>
            ))}
          </View>

          <View wrap={false} style={[styles.tableRow, { justifyContent: 'flex-end' }]}>
            <View style={[{ textAlign: 'right', paddingRight: 8 }]}>
              <Text style={[styles.fontNormal]}>SUBTOTAL</Text>
            </View>
            <View style={[styles.tableCell, { width: '13%', textAlign: 'right' }]}>
              <Text>{subtotal?.toFixed(2)}</Text>
            </View>
          </View>

          <View wrap={false} style={[styles.tableRow, { justifyContent: 'flex-end' }]}>
            <View style={[{ textAlign: 'right', paddingRight: 8 }]}>
              <Text style={[styles.fontNormal]}>ADD GST ({FloorGST}%)</Text>
            </View>
            <View style={[styles.tableCell, { width: '13%', textAlign: 'right' }]}>
              <Text>{invoice.order?.gst?.toFixed(2)}</Text>
            </View>
          </View>

          <View wrap={false} style={[styles.tableRow, { justifyContent: 'flex-end' }]}>
            <View style={[{ textAlign: 'right', paddingRight: 8 }]}>
              <Text style={[styles.fontNormal]}>ADD DELIVERY FEE</Text>
            </View>
            <View style={[styles.tableCell, { width: '13%', textAlign: 'right' }]}>
              <Text>{invoice.order?.delivery_fee?.toFixed(2)}</Text>
            </View>
          </View>

          <View wrap={false} style={[styles.tableRow, { justifyContent: 'flex-end' }]}>
            <View style={[{ textAlign: 'right', paddingRight: 8 }]}>
              <Text style={[styles.fontNormal, styles.fontWeightBold]}>TOTAL</Text>
            </View>
            <View style={[styles.tableCell, { width: '13%', textAlign: 'right' }]}>
              <Text>{invoice?.due_amount?.toFixed(2)}</Text>
            </View>
          </View>
        </View>

        <View wrap={false} style={styles.section}>
          <View style={[styles.tableRow, { gap: 4 }]}>
            <Text style={[styles.fontNormal, styles.fontWeightBold, styles.fontFamilyNoto]}>Remarks:</Text>
            <Text style={[styles.fontNormal, styles.fontFamilyNoto]}>{checkdata(invoice?.invoiceRemarks)}</Text>
          </View>
        </View>

        <View wrap={false}>
          <View style={{ marginTop: 10 }}>
            <Text style={[styles.fontNormal]}>RECEIVED THE ABOVE GOODS IN GOOD AND PROPER CONDITION</Text>
          </View>

          <View style={{ marginTop: 60 }}>
            <View style={[styles.row]}>
              <View style={[styles.signatureSection]}>
                <Text style={[styles.fontNormal]}>COMPANY’S STAMP, SIGNATURE & DATE</Text>
              </View>
              <View style={[styles.signatureSection]}>
                <Text style={[styles.fontNormal]}>FOR {checkdata(userOrg?.name)}</Text>
              </View>
            </View>
          </View>
        </View>

        <View
          fixed
          style={{
            position: 'absolute',
            bottom: 40,
            right: 40,
            textAlign: 'right',
          }}
        >
          <Text
            style={[styles.fontNormal]}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            fixed
          />
        </View>
      </Page>
    </Document>
  );
};

const stream2buffer = (stream: NodeJS.ReadableStream) => {
  return new Promise((resolve, reject) => {
    const _buf: any[] = [];

    stream.on('data', (chunk) => _buf.push(chunk));
    stream.on('end', () => resolve(Buffer.concat(_buf)));
    stream.on('error', (err) => reject(err));
  });
};

const generateInvoicePdfBuffer = async (props: InvoicePdfProps): Promise<Buffer> => {
  const stream = await pdf(<InvoicePdf {...props} />).toBuffer();
  const buffer = (await stream2buffer(stream)) as Buffer;

  return buffer;
};

export { generateInvoicePdfBuffer };
